import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { UpdatesNotepad } from "@/components/UpdatesNotepad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MetierPicker } from "@/components/metiers/MetierPicker";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, CheckCircle2, Handshake, Building2, Rocket } from "lucide-react";
import { api, formatApiError } from "@/api";
import { readBpRef, rememberBpRef, clearBpRef } from "@/pages/PartenairePublic";
import { LanguageCurrencySwitcher } from "@/components/LanguageCurrencySwitcher";
import { Money } from "@/components/Money";
import { SiretInput } from "@/components/SiretInput";
import { validateSiret } from "@/lib/siret";
import { PromoCountdown } from "@/components/PromoBanner";

export default function Inscription() {
  const [searchParams] = useSearchParams();
  const initialPlan = searchParams.get("plan") || "medium_budget";
  const initialCycle = searchParams.get("cycle") || "annual";
  const [plans, setPlans] = useState([]);
  const [submitted, setSubmitted] = useState(null);
  // Phase 78 — code partenaire (lien /partenaires/<code> ou mémorisé 30 jours)
  const [bpRef, setBpRef] = useState(() => (searchParams.get("bp") || readBpRef() || "").toUpperCase() || null);
  const [bpInfo, setBpInfo] = useState(null);
  useEffect(() => {
    if (!bpRef) { setBpInfo(null); return; }
    if (searchParams.get("bp")) rememberBpRef(bpRef);
    api.get(`/public/partenaires/${bpRef}?preview=1`).then((r) => setBpInfo(r.data)).catch(() => { setBpInfo(null); setBpRef(null); clearBpRef(); });
  }, [bpRef, searchParams]);
  const removeBp = () => { setBpRef(null); setBpInfo(null); clearBpRef(); };
  const [form, setForm] = useState({
    raison_sociale: "", chef_nom: "", chef_prenom: "", email: "", telephone: "",
    siret: "", secteur: "", plan: initialPlan, cycle: initialCycle, nb_salaries_estime: "",
    metier_code: (searchParams.get("metier") || "").toUpperCase() || null, metiers_secondaires: [], // Phase 89 — module métier (pré-rempli depuis la landing)
    message: "",
    en_projet: false,      // Phase 87 — entreprise en cours de création (pas encore de SIRET)
    custom_amount: null,  // v2.0.07 pricing dynamique
    promotion_code: "",   // v2.0.17 Phase 37 — code promo
    legal_accepted: false, // Phase 71 — CGV / CGU
  });
  const [submitting, setSubmitting] = useState(false);
  const [promoResult, setPromoResult] = useState(null); // { ok, final_price, base_price, discount_amount, saving_pct, promotion_name }
  const [promoChecking, setPromoChecking] = useState(false);

  // v2.0.05 — ne charge QUE les offres v2 (TOP/MEDIUM/STANDARD/MAXIMUM),
  // les anciennes formules Solo/Business/Association/Syndicat ne sont plus proposées.
  useEffect(() => {
    api.get("/public/plans?version=v2")
      .then((r) => setPlans(r.data || []))
      .catch(() => {});
  }, []);

  // v2.0.17 (Phase 37) — Résout automatiquement la meilleure promo (code OU auto-apply)
  // à chaque changement de plan / cycle / code.
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!form.plan || !form.cycle) return;
      setPromoChecking(true);
      try {
        const { data } = await api.post("/public/promotions/validate", {
          plan: form.plan, cycle: form.cycle, code: form.promotion_code || null,
          email: form.email || null,
        });
        setPromoResult(data);
      } catch {
        setPromoResult(null);
      } finally {
        setPromoChecking(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.plan, form.cycle, form.promotion_code, form.email]);

  const submit = async (e) => {
    e.preventDefault();
    let siret = null;
    if (!form.en_projet) {
      const siretCheck = validateSiret(form.siret);
      if (siretCheck.state !== "valid") { toast.error(siretCheck.message || "SIRET invalide"); return; }
      siret = siretCheck.cleaned;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, siret, nb_salaries_estime: form.nb_salaries_estime ? parseInt(form.nb_salaries_estime) : null, bp_code: bpRef || null };
      const { data } = await api.post("/public/inscription", payload);
      if (bpRef) clearBpRef();
      // Phase 84 : plus de redirection automatique vers PayPal — la page de paiement laisse le choix
      // entre carte bancaire (Stripe) et PayPal.
      setSubmitted(data);
      toast.success("Demande enregistrée — choisissez votre moyen de paiement");
      setTimeout(() => { window.location.href = `/pay/checkout/${data.id}`; }, 1200);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    const selectedPlan = plans.find((p) => p.key === submitted.plan) || {};
    const price = submitted.cycle === "annual" ? selectedPlan.annual_price : selectedPlan.monthly_price;
    return (
      <div className="min-h-screen bg-white">
        <header className="border-b border-[#DEE2E6]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3"><img src="/pag-logo.png" alt="PAG" className="h-10 w-10 object-contain" /><span className="font-display font-black text-xl">PAG</span></Link>
          </div>
        </header>
        <main className="max-w-2xl mx-auto p-6 lg:p-12" data-testid="inscription-success">
          <div className="bg-[#059669] text-white p-10 lg:p-12 border border-[#0A0A0A]">
            <CheckCircle2 className="h-12 w-12 text-[#FFD700] mb-6" />
            <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-3">// Dernière étape : le paiement</div>
            <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter leading-[1] mb-6">
              Choisissez votre moyen de paiement.
            </h1>
            <p className="text-base lg:text-lg text-white/90 leading-relaxed mb-6">
              Votre demande a été acceptée. Vous allez être redirigé(e) vers la page de paiement sécurisée :
              <b> carte bancaire (Stripe)</b> ou <b>PayPal</b>, à vous de choisir. Votre compte sera activé dès la confirmation du paiement.
            </p>
            <div className="pt-6 border-t border-white/20 font-mono text-sm space-y-1">
              <div>Référence : <b>{submitted.id.slice(0, 8).toUpperCase()}</b></div>
              <div>Plan : <b>{selectedPlan.name || submitted.plan}</b></div>
              <div>Formule : <b>{submitted.cycle === "annual" ? (price ? `Annuel — ${price}€/an` : "Annuel — sur devis") : (price ? `Mensuel — ${price}€/mois` : "Mensuel — sur devis")}</b></div>
              <div className="text-white/70 text-xs mt-2">Renouvellement automatique · Résiliable à tout moment</div>
            </div>
          </div>
          <div className="mt-6 flex gap-3 flex-wrap">
            <a href={`/pay/checkout/${submitted.id}`} data-testid="inscription-pay-page" className="inline-flex items-center h-12 px-6 bg-[#FFD700] hover:bg-[#F0C800] text-[#0A0A0A] font-semibold text-sm">
              Carte bancaire ou PayPal →
            </a>
            <Link to="/" className="inline-flex items-center h-12 px-6 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white font-semibold text-sm transition-colors">Retour à l'accueil</Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-[#DEE2E6]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3"><img src="/pag-logo.png" alt="PAG" className="h-10 w-10 object-contain" /><span className="font-display font-black text-xl">PAG</span></Link>
          <div className="flex items-center gap-3">
            <LanguageCurrencySwitcher />
            <Link to="/" className="text-sm font-semibold flex items-center gap-2 hover:text-[#002FA7]"><ArrowLeft className="h-4 w-4" /> Accueil</Link>
          </div>
        </div>
      </header>

      <section className="border-b border-[#DEE2E6] bg-[#0A0A0A] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-16">
          <div className="text-xs uppercase tracking-[0.3em] text-[#FFD700] font-semibold mb-3">// Souscription</div>
          <h1 className="font-display text-4xl lg:text-6xl font-black tracking-tighter leading-[1]">Rejoignez PAG.</h1>
          <p className="mt-4 text-base text-white/70 max-w-2xl">Remplissez le formulaire — un administrateur valide votre compte sous 24h.</p>
        </div>
      </section>

      <main className="max-w-3xl mx-auto p-6 lg:p-12">
        <UpdatesNotepad mode="signup" className="mb-6" compact />
        {bpInfo && (
          <div className="mb-6 border-2 border-[#0A0A0A] bg-[#F8F9FA] p-4 flex flex-wrap items-center gap-3" data-testid="ins-bp-banner">
            {bpInfo.photo_url ? <img src={bpInfo.photo_url} alt="" className="h-10 w-10 object-cover" /> : <Handshake className="h-6 w-6 text-[#002FA7]" />}
            <div className="flex-1 text-sm"><b>Accompagné(e) par {bpInfo.prenom} {bpInfo.nom}</b> — Business Partner PAG <span className="font-mono text-xs" data-testid="ins-bp-code">{bpInfo.code}</span>. Votre souscription lui sera rattachée ; prix et service identiques.</div>
            <button type="button" onClick={removeBp} className="text-[10px] uppercase tracking-wider font-bold text-[#868E96] hover:text-[#E60000]" data-testid="ins-bp-remove">Retirer</button>
          </div>
        )}
        <form onSubmit={submit} className="space-y-6" data-testid="inscription-form">
          <Section title="1. Votre entreprise">
            <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Situation" data-testid="ins-type">
              {[["entreprise", "Entreprise existante", "J'ai un SIRET", Building2], ["projet", "Projet", "Création en cours, pas encore de SIRET", Rocket]].map(([k, l, sub, I]) => {
                const on = (form.en_projet ? "projet" : "entreprise") === k;
                return (
                  <button type="button" key={k} role="radio" aria-checked={on} onClick={() => setForm({ ...form, en_projet: k === "projet", siret: k === "projet" ? "" : form.siret })} data-testid={`ins-type-${k}`}
                    className={`text-left p-3 border-2 transition ${on ? "border-[#0A0A0A] bg-[#F8F9FA]" : "border-[#DEE2E6] hover:border-[#868E96]"}`}>
                    <div className="flex items-center gap-2 font-bold text-sm"><I className={`h-4 w-4 ${on ? "text-[#002FA7]" : "text-[#868E96]"}`} />{l}</div>
                    <div className="text-[11px] text-[#495057] mt-0.5">{sub}</div>
                  </button>
                );
              })}
            </div>
            {form.en_projet && (
              <div className="border-l-4 border-[#FFD700] bg-[#FFFBEB] p-3 text-xs text-[#92400E]" data-testid="ins-projet-note">
                Pas de SIRET ni de KBIS demandés pour l'instant : vous les renseignerez dans votre profil dès l'immatriculation de votre entreprise.
              </div>
            )}
            <F label={form.en_projet ? "Nom du projet" : "Raison sociale"} value={form.raison_sociale} onChange={(v) => setForm({ ...form, raison_sociale: v })} required testid="ins-rs" />
            <div className="grid grid-cols-2 gap-3">
              {!form.en_projet && <SiretInput value={form.siret} onChange={(v) => setForm({ ...form, siret: v })} required testid="ins-siret" hint="14 chiffres — contrôle de la clé à la saisie" />}
              <F label="Nb salariés estimé" type="number" value={form.nb_salaries_estime} onChange={(v) => setForm({ ...form, nb_salaries_estime: v })} testid="ins-nb" />
            </div>
            <div data-testid="ins-metier-block">
              <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Votre métier <span className="text-[#868E96] normal-case tracking-normal font-normal">(module métier PAG · secondaires facultatifs)</span></Label>
              <div className="mt-1 border border-[#DEE2E6] p-3 bg-[#F8F9FA]">
                <MetierPicker value={form.metier_code} onChange={(code, m) => setForm({ ...form, metier_code: code, secteur: m?.label || "" })} secondaires={form.metiers_secondaires} onSecondairesChange={(v) => setForm({ ...form, metiers_secondaires: v })} testid="ins-metier" />
              </div>
            </div>
          </Section>

          <Section title="2. Contact">
            <div className="grid grid-cols-2 gap-3">
              <F label="Nom" value={form.chef_nom} onChange={(v) => setForm({ ...form, chef_nom: v })} required testid="ins-nom" />
              <F label="Prénom" value={form.chef_prenom} onChange={(v) => setForm({ ...form, chef_prenom: v })} required testid="ins-prenom" />
              <F label="Email pro" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="ins-email" />
              <F label="Téléphone pro" type="tel" value={form.telephone} onChange={(v) => setForm({ ...form, telephone: v })} required testid="ins-tel" />
            </div>
          </Section>

          <Section title="3. Formule choisie">
            {/* Toggle cycle EN HAUT pour que l'utilisateur choisisse d'abord Annuel/Mensuel */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <button type="button" onClick={() => setForm({ ...form, cycle: "annual" })} data-testid="ins-cycle-annual" className={`p-3 border-2 ${form.cycle === "annual" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#DEE2E6] hover:border-[#0A0A0A]"}`}>
                <div className="text-xs uppercase tracking-wider font-bold">Annuel</div>
                <div className="text-[10px] opacity-70 mt-1">Engagement 1 an</div>
              </button>
              <button type="button" onClick={() => setForm({ ...form, cycle: "monthly" })} data-testid="ins-cycle-monthly" className={`p-3 border-2 ${form.cycle === "monthly" ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#DEE2E6] hover:border-[#0A0A0A]"}`}>
                <div className="text-xs uppercase tracking-wider font-bold">Mensuel</div>
                <div className="text-[10px] opacity-70 mt-1">Sans engagement</div>
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {plans.map((p) => {
                const rawPrice = form.cycle === "annual" ? p.annual_price : p.monthly_price;
                const rawMin = form.cycle === "annual" ? p.annual_price_min : p.monthly_price_min;
                const rawMax = form.cycle === "annual" ? p.annual_price_max : p.monthly_price_max;
                const isYellow = (p.color || "").toLowerCase() === "#ffd700";
                const unit = form.cycle === "annual" ? "an" : "mois";
                // v2.0.06 : priorité à la fourchette min–max (plans v2 "sur devis"),
                // sinon prix unique (plans v1 ou plans v2 sans fourchette).
                let display;
                const hasRange = rawMin != null && rawMax != null && Number(rawMin) !== Number(rawMax);
                if (hasRange) {
                  display = <><Money amount={Math.round(rawMin)} showOriginal={false} />–<Money amount={Math.round(rawMax)} showOriginal={false} /><span className="text-xs font-normal text-[#868E96]">/{unit}</span></>;
                } else if (rawPrice != null) {
                  display = <><Money amount={rawPrice} showOriginal={false} /><span className="text-xs font-normal text-[#868E96]">/{unit}</span></>;
                } else if (rawMin != null) {
                  display = <><Money amount={Math.round(rawMin)} showOriginal={false} />+<span className="text-xs font-normal text-[#868E96]">/{unit}</span></>;
                } else {
                  display = <span className="text-sm font-normal text-[#868E96]">Sur devis</span>;
                }
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setForm({ ...form, plan: p.key })}
                    data-testid={`ins-plan-${p.key}`}
                    className={`p-3 border-2 text-left transition-colors ${form.plan === p.key ? "border-[#002FA7] bg-[#002FA7]/5" : "border-[#DEE2E6] hover:border-[#0A0A0A]"}`}
                  >
                    <div className={`text-[10px] uppercase tracking-wider font-bold ${isYellow ? "text-[#0A0A0A] bg-[#FFD700] inline-block px-1.5 py-0.5" : ""}`} style={!isYellow ? { color: p.color } : {}}>{p.name.replace("PAG ", "")}</div>
                    <div className="font-display font-black text-lg mt-1">{display}</div>
                  </button>
                );
              })}
            </div>
            {/* v2.0.07 — Pricing Dynamique : slider dans la fourchette du plan choisi */}
            {(() => {
              const selected = plans.find((p) => p.key === form.plan);
              if (!selected) return null;
              const min = form.cycle === "annual" ? selected.annual_price_min : selected.monthly_price_min;
              const max = form.cycle === "annual" ? selected.annual_price_max : selected.monthly_price_max;
              const defPrice = form.cycle === "annual" ? selected.annual_price : selected.monthly_price;
              if (min == null || max == null || Number(min) === Number(max)) return null;
              const unit = form.cycle === "annual" ? "an" : "mois";
              const value = form.custom_amount ?? defPrice ?? Math.round((Number(min) + Number(max)) / 2);
              return (
                <div className="mt-4 p-4 border-2 border-[#FFD700] bg-[#FFF9DB]" data-testid="pricing-dynamique">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#0A0A0A] mb-2">
                    🎯 Pricing dynamique — {selected.name.replace("PAG ", "")}
                  </div>
                  <div className="text-xs text-[#495057] mb-3">
                    Choisissez le montant qui vous convient dans la fourchette <Money amount={Math.round(min)} showOriginal={false} /> – <Money amount={Math.round(max)} showOriginal={false} />/{unit}.
                    Vous ajustez selon vos besoins réels (nombre d'utilisateurs, modules).
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-mono text-[#868E96]"><Money amount={Math.round(min)} showOriginal={false} /></span>
                    <input
                      type="range"
                      min={Math.round(min)}
                      max={Math.round(max)}
                      step={form.cycle === "annual" ? 10 : 1}
                      value={Math.round(value)}
                      onChange={(e) => setForm({ ...form, custom_amount: Number(e.target.value) })}
                      className="flex-1 accent-[#002FA7]"
                      data-testid="pricing-slider"
                    />
                    <span className="text-[10px] font-mono text-[#868E96]"><Money amount={Math.round(max)} showOriginal={false} /></span>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="font-display font-black text-3xl text-[#002FA7]" data-testid="pricing-value">
                      <Money amount={Math.round(value)} showOriginal={false} /><span className="text-lg font-normal text-[#868E96]">/{unit}</span>
                    </div>
                    {form.custom_amount != null && form.custom_amount !== defPrice && (
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, custom_amount: null })}
                        className="text-[10px] uppercase tracking-wider font-bold underline text-[#495057] hover:text-[#0A0A0A]"
                        data-testid="pricing-reset"
                      >
                        Réinitialiser au prix par défaut (<Money amount={defPrice} showOriginal={false} />/{unit})
                      </button>
                    )}
                  </div>
                  {/* v2.0.08 — Pricing presets rapides */}
                  <div className="mt-4 pt-4 border-t border-[#FFD700]">
                    <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#495057] mb-2">Presets rapides</div>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { key: "eco", label: "Économique", desc: "Prix mini", value: Math.round(Number(min)) },
                        { key: "std", label: "Standard", desc: "Milieu de gamme", value: Math.round((Number(min) + Number(max)) / 2) },
                        { key: "premium", label: "Premium", desc: "Toutes options", value: Math.round(Number(max)) },
                      ].map((preset) => (
                        <button
                          key={preset.key}
                          type="button"
                          onClick={() => setForm({ ...form, custom_amount: preset.value })}
                          data-testid={`pricing-preset-${preset.key}`}
                          className={`p-2 border-2 text-left transition-colors ${Math.round(value) === preset.value ? "border-[#002FA7] bg-white" : "border-[#DEE2E6] bg-white hover:border-[#0A0A0A]"}`}
                        >
                          <div className="text-[10px] uppercase tracking-wider font-bold">{preset.label}</div>
                          <div className="text-[9px] text-[#868E96]">{preset.desc}</div>
                          <div className="font-display font-black text-base mt-1"><Money amount={preset.value} showOriginal={false} /></div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}
          </Section>

          <Section title="4. Message (optionnel)">
            <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} placeholder="Précisez vos besoins…" data-testid="ins-message" />
          </Section>

          {/* v2.0.17 (Phase 37) — Code promo + preview */}
          <Section title="5. Code promo (facultatif)">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Code promo</Label>
                <Input
                  value={form.promotion_code}
                  onChange={(e) => setForm({ ...form, promotion_code: e.target.value.toUpperCase().trim() })}
                  placeholder="SUMMER15"
                  data-testid="ins-promo-code"
                  className="h-10 font-mono tracking-wider"
                />
              </div>
              <div className="text-[10px] text-[#495057] pb-2">
                {promoChecking && <span>Vérification…</span>}
              </div>
            </div>
            {promoResult?.ok && (
              <div className="mt-3 p-3 border-2 border-[#059669] bg-[#F0FDF4] flex items-center gap-3" data-testid="ins-promo-result">
                <div className="h-8 w-8 bg-[#059669] text-white flex items-center justify-center text-lg font-black">✓</div>
                <div className="flex-1">
                  <div className="font-display font-bold text-sm">
                    {promoResult.promotion_name}
                    <span className="ml-2 text-[10px] font-normal text-[#059669]">
                      {promoResult.auto_apply ? "AUTO" : "CODE"}
                    </span>
                  </div>
                  <div className="text-xs text-[#495057]">
                    <s className="text-[#868E96]">{promoResult.base_price}€</s>{" → "}
                    <b className="text-[#059669] text-base">{promoResult.final_price}€</b>{" · "}
                    Économie <b className="text-[#F59E0B]">-{promoResult.discount_amount}€</b> ({promoResult.saving_pct}%)
                  </div>
                  <PromoCountdown
                    endsInSeconds={promoResult.ends_in_seconds}
                    endsAt={promoResult.ends_at}
                    className="text-[11px] text-[#F59E0B] mt-1 font-semibold"
                    testid="ins-promo-countdown"
                  />
                </div>
              </div>
            )}
            {promoResult && !promoResult.ok && form.promotion_code && (
              <div className="mt-3 p-3 border border-[#E60000] bg-[#FEF2F2] text-xs text-[#E60000]" data-testid="ins-promo-invalid">
                Code invalide ou non applicable au plan / cycle sélectionné.
              </div>
            )}
          </Section>

          <label className="flex items-start gap-3 p-4 border border-[#DEE2E6] bg-white text-sm cursor-pointer" data-testid="ins-legal-label">
            <input type="checkbox" required checked={form.legal_accepted} onChange={(e) => setForm({ ...form, legal_accepted: e.target.checked })} data-testid="ins-legal-accept" className="mt-1 h-4 w-4" />
            <span>J'ai lu et j'accepte les <a href="/legal/cgv" target="_blank" rel="noreferrer" className="underline font-semibold hover:text-[#002FA7]" data-testid="ins-legal-cgv">Conditions générales de vente</a> et les <a href="/legal/cgu" target="_blank" rel="noreferrer" className="underline font-semibold hover:text-[#002FA7]" data-testid="ins-legal-cgu">Conditions générales d'utilisation</a>. Les options ajoutées ultérieurement à ma demande s'ajoutent au prix de l'abonnement.</span>
          </label>

          <Button type="submit" disabled={submitting || !form.legal_accepted} data-testid="ins-submit" className="w-full h-12 bg-[#002FA7] hover:bg-[#002175] text-white font-semibold">
            {submitting ? "Envoi…" : "Envoyer ma demande"} <ArrowUpRight className="ml-2 h-4 w-4" />
          </Button>
        </form>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return <div className="bg-white border border-[#DEE2E6] p-6 space-y-3"><div className="text-xs uppercase tracking-[0.25em] font-bold mb-3 flex items-center gap-2"><span className="h-px bg-[#0A0A0A] w-6" />{title}</div>{children}</div>;
}
function F({ label, value, onChange, type = "text", required, testid }) {
  return <div className="space-y-1.5"><Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">{label}{required && <span className="text-[#E60000]"> *</span>}</Label><Input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid} className="h-10" /></div>;
}
